import React, { memo, useRef, useState } from 'react';
import { ShoppingCart } from 'lucide-react';
import { Product } from '../types';
import { useCartActions } from '../hooks/useCart';
import { isCustomAmountProduct, setCustomAmount } from '../utils/customAmount';

interface ProductCardProps {
  product: Product;
}

const ProductCard: React.FC<ProductCardProps> = ({ product }) => {
  const { addToCart } = useCartActions();
  const addingRef = useRef(false);
  const [customAmount, setCustomAmountInput] = useState<string>('');
  const usesCustomAmount = isCustomAmountProduct(product);

  const handleAddToCart = () => {
    if (addingRef.current || product.stock === 0) return;

    if (usesCustomAmount) {
      const amount = parseFloat(customAmount) || 0;
      if (amount < 1) {
        alert('Please enter an amount of at least $1.00');
        return;
      }
      setCustomAmount(product.id, amount.toFixed(2));
    }

    addingRef.current = true;
    void addToCart(product, 1).finally(() => {
      addingRef.current = false;
    });
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(price);
  };

  return (
    <div className="flex flex-col" style={{ height: '100%' }}>
      <div className="relative w-full" style={{ paddingBottom: '100%', backgroundColor: '#0f0f0f', borderRadius: '8px', overflow: 'hidden' }}>
        <img
          src={product.image}
          alt={product.name}
          className="absolute inset-0 w-full h-full select-none"
          style={{ objectFit: 'contain' }}
          loading="lazy"
          draggable={false}
          onError={(e) => {
            const target = e.target as HTMLImageElement;
            target.src = 'https://via.placeholder.com/400x400?text=No+Image';
          }}
        />
      </div>

      <div className="mt-3 flex flex-col flex-1">
        <h3
          className="text-lg font-semibold mb-3"
          style={{
            color: '#e0e0e0',
            fontFamily: "'911 Porscha', sans-serif",
            fontStyle: 'italic',
            letterSpacing: 'normal',
            lineHeight: '1.3',
            minHeight: '2.8rem',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {product.name.toUpperCase()}
        </h3>

        {product.description && (
          <p className="text-sm mb-2" style={{ color: '#b0b0b0' }}>
            {product.description}
          </p>
        )}

        <div className="flex items-center justify-between mt-auto pt-2">
          <div className="flex items-center" style={{ height: '36px' }}>
            {usesCustomAmount ? (
              <div className="flex items-center">
                <span className="font-bold mr-1" style={{ color: '#da70d6', fontSize: '20px', lineHeight: '36px' }}>$</span>
                <input
                  type="text"
                  inputMode="decimal"
                  value={customAmount}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
                      setCustomAmountInput(value);
                    }
                  }}
                  className="border rounded px-2"
                  style={{
                    backgroundColor: '#1a1a1a',
                    borderColor: '#39ff14',
                    color: '#da70d6',
                    fontSize: '20px',
                    fontWeight: 'bold',
                    width: '75px',
                    height: '36px',
                    lineHeight: '36px',
                    textAlign: 'left',
                  }}
                  placeholder="0.00"
                />
              </div>
            ) : (
              <span className="text-xl font-bold" style={{ color: '#da70d6', lineHeight: '36px' }}>
                {formatPrice(product.price)}
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={handleAddToCart}
            disabled={product.stock === 0}
            className="flex items-center justify-center gap-1 px-3 py-1.5 rounded-md text-xs font-bold cursor-pointer disabled:cursor-not-allowed shrink-0"
            style={{
              backgroundColor: product.stock === 0 ? '#2a2a2a' : '#39ff14',
              color: product.stock === 0 ? '#666666' : '#0a0a0a',
              minWidth: '7.5rem',
            }}
          >
            <ShoppingCart className="h-3 w-3 shrink-0" />
            <span>{product.stock === 0 ? 'OUT OF STOCK' : 'ADD TO CART'}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default memo(ProductCard);
